import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Database, User, CheckCircle, Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Install() {
  const navigate = useNavigate();
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({
    admin_email: '',
    admin_name: '',
    admin_password: '',
    admin_password_confirm: '',
    database_url: '',
  });

  useEffect(() => {
    // Vérifier si l'application est déjà installée au chargement
    api.get('/install/status')
      .then(res => {
        if (res.data.installed) {
          toast.error('Application déjà installée.');
          navigate('/admin');
        } else {
          setCheckingStatus(false);
        }
      })
      .catch(() => {
        // En cas d'erreur de réseau/connexion mais non installée, on laisse l'accès pour tenter l'install
        setCheckingStatus(false);
      });
  }, [navigate]);

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const validateStep2 = () => {
    const name = form.admin_name.trim();
    const email = form.admin_email.trim();
    if (!name) return 'Le nom est obligatoire';
    if (!EMAIL_RE.test(email)) return 'Email invalide';
    if (form.admin_password.length < 8) return 'Mot de passe : 8 caractères minimum';
    if (form.admin_password !== form.admin_password_confirm) return 'Les mots de passe ne correspondent pas';
    return null;
  };

  const handleInstall = async () => {
    const err = validateStep2();
    if (err) {
      toast.error(err);
      return;
    }

    setLoading(true);
    try {
      // Appeler la route d'installation complète qui exécute les migrations et les seeds,
      // puis connecte l'admin automatiquement via un cookie session_token.
      await api.post('/install', {
        admin_name: form.admin_name.trim(),
        admin_email: form.admin_email.trim().toLowerCase(),
        admin_password: form.admin_password,
      });
      setStep(3);
      toast.success('Installation réussie !');
      // L'utilisateur est maintenant connecté via le cookie session_token retourné par /install
      setTimeout(() => navigate('/admin'), 2000);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 409) {
        toast.error('Cet email est déjà utilisé. Connectez-vous à la place.');
      } else {
        toast.error(detail || "Erreur lors de l'installation");
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-garden-800 to-garden-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8" data-testid="install-page">
        {/* Logo */}
        <div className="text-center mb-8">
          <span className="font-accent text-garden-800 text-4xl">Côté Terrasse</span>
          <p className="text-charcoal/60 mt-2">Assistant d'installation</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-3 h-3 rounded-full transition-all ${
                step >= s ? 'bg-garden-600' : 'bg-garden-200'
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 text-garden-800 mb-4">
              <Database size={24} />
              <h2 className="font-heading font-semibold text-xl">Configuration de la base de données</h2>
            </div>
            <p className="text-sm text-charcoal/70 mb-4">
              Entrez l'URL de connexion à votre base de données PostgreSQL.
              Laissez vide pour utiliser SQLite (développement uniquement).
            </p>
            <div>
              <Label className="text-sm">URL de la base de données (optionnel)</Label>
              <Input
                value={form.database_url}
                onChange={(e) => setForm({ ...form, database_url: e.target.value })}
                placeholder="postgresql://user:password@host:5432/dbname"
                className="border-garden-200 rounded-xl mt-1"
                data-testid="install-database-url"
              />
              <p className="text-xs text-charcoal/50 mt-1">
                Format : postgresql://utilisateur:motdepasse@hôte:port/base
              </p>
            </div>
            <Button
              onClick={() => setStep(2)}
              className="w-full bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6"
              data-testid="install-step1-continue"
            >
              Continuer
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 text-garden-800 mb-4">
              <User size={24} />
              <h2 className="font-heading font-semibold text-xl">Créer le compte administrateur</h2>
            </div>
            <p className="text-sm text-charcoal/70 mb-4">
              Entrez les informations du premier administrateur.
              Ce compte aura tous les droits sur le site.
            </p>
            <div>
              <Label className="text-sm">Nom complet *</Label>
              <Input
                value={form.admin_name}
                onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                placeholder="Jean Dupont"
                className="border-garden-200 rounded-xl mt-1"
                data-testid="install-admin-name"
              />
            </div>
            <div>
              <Label className="text-sm">Email *</Label>
              <Input
                type="email"
                autoComplete="email"
                value={form.admin_email}
                onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                placeholder="jean@exemple.com"
                className="border-garden-200 rounded-xl mt-1"
                data-testid="install-admin-email"
              />
              <p className="text-xs text-charcoal/50 mt-1">
                Vous utiliserez cet email pour vous connecter à l'administration.
              </p>
            </div>
            <div>
              <Label className="text-sm">Mot de passe *</Label>
              <div className="relative">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={form.admin_password}
                  onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                  placeholder="Minimum 8 caractères"
                  className="border-garden-200 rounded-xl mt-1 pr-10"
                  data-testid="install-admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal/70"
                  aria-label={showPwd ? 'Masquer' : 'Afficher'}
                  data-testid="install-password-toggle"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-sm">Confirmer le mot de passe *</Label>
              <Input
                type={showPwd ? 'text' : 'password'}
                autoComplete="new-password"
                value={form.admin_password_confirm}
                onChange={(e) => setForm({ ...form, admin_password_confirm: e.target.value })}
                placeholder="Saisissez à nouveau le mot de passe"
                className="border-garden-200 rounded-xl mt-1"
                data-testid="install-admin-password-confirm"
              />
              <p className="text-xs text-charcoal/50 mt-1">
                Vous pourrez aussi activer la connexion Google plus tard via les paramètres.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 rounded-full"
                data-testid="install-step2-back"
              >
                Retour
              </Button>
              <Button
                onClick={handleInstall}
                disabled={loading}
                className="flex-1 bg-garden-800 hover:bg-garden-900 text-white rounded-full"
                data-testid="install-submit"
              >
                {loading ? 'Installation...' : 'Installer'}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="text-center space-y-6" data-testid="install-success">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="font-heading font-semibold text-xl text-garden-800">
              Installation terminée !
            </h2>
            <p className="text-charcoal/70">
              Votre compte administrateur a été créé. Redirection vers l'administration...
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-garden-100 text-center">
          <p className="text-xs text-charcoal/50">
            Côté Terrasse - Site vitrine administrable
          </p>
        </div>
      </div>
    </div>
  );
}
